<template>
  <div
    class="min-h-screen bg-zinc-50 font-sans selection:bg-sky-500/20 selection:text-sky-600 dark:bg-zinc-950 transition-colors duration-300">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
    <link
      href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@20..48,400,0,0"
      rel="stylesheet" />

    <HeaderNav />

    <div aria-hidden="true" class="h-32"></div>

    <main class="relative z-10">
      <section class="relative px-6 pb-16 pt-8 text-center lg:px-8">
        <div class="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80" aria-hidden="true">
          <div
            class="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-sky-200 to-zinc-200 opacity-40 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem] dark:from-[#0c4a6e] dark:to-[#18181b] dark:opacity-20">
          </div>
        </div>

        <div class="mx-auto max-w-2xl">
          <div class="mb-6 flex justify-center">
            <span
              class="inline-flex items-center gap-2 rounded-full border border-zinc-200/60 bg-white/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-zinc-600 backdrop-blur-md dark:border-white/10 dark:bg-white/5 dark:text-zinc-300 shadow-sm">
              <span class="material-symbols-rounded text-[16px] text-sky-500">palette</span>
              Pricing Studio
            </span>
          </div>

          <h1 class="text-4xl font-bold tracking-tight text-zinc-900 sm:text-5xl md:text-6xl dark:text-white">
            Plans for every <br class="hidden sm:block" />
            <span class="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-indigo-500">color workflow.</span>
          </h1>

          <p class="mt-6 text-lg leading-relaxed text-zinc-600 dark:text-zinc-400 max-w-xl mx-auto">
            Create palettes, test harmonies, and export visuals.
            All plans include full access to the palette playground.
          </p>

          <p class="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
            Includes <span class="font-semibold text-zinc-900 dark:text-white">1 day free trial</span> on eligible plans.
          </p>
        </div>
      </section>

      <section class="mx-auto max-w-6xl px-4 pb-24 lg:px-8">
        <div class="grid grid-cols-1 mx-auto gap-8 lg:grid-cols-2 items-start">
          <article class="relative flex flex-col rounded-[2rem] shadow-2xl transition-transform duration-300 hover:scale-[1.01] group">
            <div
              class="absolute inset-0 overflow-hidden rounded-[2rem] bg-zinc-900 dark:bg-zinc-50 ring-1 ring-zinc-900/10 dark:ring-zinc-200/20 border-2 border-indigo-500/20 dark:border-indigo-400/30">
              <div class="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-indigo-500/20 blur-3xl pointer-events-none"></div>
            </div>

            <div class="absolute -top-4 left-8 z-20">
              <span
                class="inline-flex items-center gap-1 rounded-full bg-indigo-500 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white shadow-lg shadow-indigo-500/40">
                <span class="material-symbols-rounded text-[14px]">rocket_launch</span>
                Launch Offer
              </span>
            </div>

            <div class="relative z-10 p-8 md:p-10 flex flex-col h-full">
              <div class="mb-6 border-b border-white/10 pb-6 dark:border-zinc-200/50 relative">
                <div class="flex items-center justify-between gap-x-4">
                  <h3 class="text-xl font-semibold leading-8 text-white dark:text-zinc-900">Creator</h3>

                  <div class="flex items-center gap-2">
                    <div
                      class="rounded-full bg-green-500/10 border border-green-500/20 px-3 py-1 text-xs font-bold text-green-400 dark:text-green-600">
                      Save 65%
                    </div>

                    <div
                      class="rounded-full bg-sky-500/10 border border-sky-500/20 px-3 py-1 text-xs font-bold text-sky-300 dark:text-sky-600">
                      1 day free trial
                    </div>
                  </div>
                </div>

                <p class="mt-2 text-sm leading-6 text-zinc-400 dark:text-zinc-500">
                  Try 1 day free, then keep your launch price locked.
                </p>

                <div class="mt-6 flex flex-col">
                  <span
                    class="text-sm font-medium text-zinc-500 dark:text-zinc-400 line-through decoration-zinc-600 dark:decoration-zinc-400 decoration-1">
                    $29.00/mo
                  </span>

                  <div class="flex items-baseline gap-x-1">
                    <span class="text-5xl font-bold tracking-tight text-white dark:text-zinc-900">
                      {{ priceText('pro') }}
                    </span>
                    <span class="text-sm font-medium text-zinc-400 dark:text-zinc-500">/month</span>
                  </div>

                  <span class="text-[10px] font-medium text-indigo-400 mt-1 uppercase tracking-wide">
                    * Limited time only
                  </span>
                </div>
              </div>

              <ul role="list" class="space-y-4 text-sm leading-6 text-zinc-300 dark:text-zinc-600 flex-grow">
                <li class="flex gap-x-3 items-start">
                  <span class="material-symbols-rounded text-indigo-400 dark:text-indigo-600 shrink-0" style="font-size: 20px;">
                    check_circle
                  </span>
                  <span class="font-medium text-white dark:text-zinc-900">All Launch Features</span>
                </li>
                <li class="flex gap-x-3 items-start">
                  <span class="material-symbols-rounded text-zinc-500 dark:text-zinc-400 shrink-0" style="font-size: 20px;">
                    check_circle
                  </span>
                  Unlimited palette creations
                </li>
                <li class="flex gap-x-3 items-start">
                  <span class="material-symbols-rounded text-zinc-500 dark:text-zinc-400 shrink-0" style="font-size: 20px;">
                    check_circle
                  </span>
                  Full access to playground scenes
                </li>
                <li class="flex gap-x-3 items-start">
                  <span class="material-symbols-rounded text-zinc-500 dark:text-zinc-400 shrink-0" style="font-size: 20px;">
                    check_circle
                  </span>
                  Advanced harmony & contrast tools
                </li>
                <li class="flex gap-x-3 items-start">
                  <span class="material-symbols-rounded text-zinc-500 dark:text-zinc-400 shrink-0" style="font-size: 20px;">
                    check_circle
                  </span>
                  Export PNG, HEX, JSON & Tailwind
                </li>
              </ul>

              <button
                @click="startSubscription('creator')"
                :disabled="loading === 'creator' || !planIds.creator"
                class="mt-8 block w-full rounded-full bg-gradient-to-r from-indigo-500 to-sky-500 hover:from-indigo-400 hover:to-sky-400 px-3 py-3.5 text-center text-sm font-semibold text-white shadow-[0_6px_20px_rgba(99,102,241,0.3)] hover:shadow-[0_10px_30px_rgba(99,102,241,0.4)] disabled:opacity-50 transition-all active:scale-[0.98]">
                {{ loading === 'creator' ? 'Redirecting...' : 'Start 1-Day Free Trial' }}
              </button>

              <p class="mt-3 text-center text-[11px] text-zinc-400 dark:text-zinc-500">
                Cancel anytime during the trial.
              </p>
            </div>
          </article>

          <article
            class="relative flex flex-col rounded-[2rem] border border-zinc-200 bg-white/70 p-8 shadow-xl backdrop-blur-xl dark:border-zinc-700 dark:bg-zinc-900/60 md:p-10 transition-transform duration-300 hover:scale-[1.01]">
            <div class="mb-6 border-b border-zinc-100 pb-6 dark:border-zinc-800">
              <div class="flex items-center justify-between gap-x-4">
                <h3 class="text-xl font-semibold leading-8 text-zinc-900 dark:text-white">Founder</h3>
                <div class="rounded-full bg-zinc-100 px-3 py-1 text-xs font-semibold text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                  Annual
                </div>
              </div>

              <p class="mt-2 text-sm leading-6 text-zinc-500 dark:text-zinc-400">
                Best for power users and teams. Save 20%.
              </p>

              <div class="mt-6 flex items-baseline gap-x-1">
                <span class="text-5xl font-bold tracking-tight text-zinc-900 dark:text-white">{{ priceText('teams') }}</span>
                <span class="text-sm font-medium text-zinc-500 dark:text-zinc-400">/year</span>
              </div>
            </div>

            <ul role="list" class="space-y-4 text-sm leading-6 text-zinc-600 dark:text-zinc-300">
              <li class="flex gap-x-3 items-start">
                <span class="material-symbols-rounded text-sky-500 shrink-0" style="font-size: 20px;">check_circle</span>
                <span class="font-medium text-zinc-900 dark:text-white">Everything in Creator</span>
              </li>
              <li class="flex gap-x-3 items-start">
                <span class="material-symbols-rounded text-sky-500 shrink-0" style="font-size: 20px;">check_circle</span>
                Unlimited folders & projects
              </li>
              <li class="flex gap-x-3 items-start">
                <span class="material-symbols-rounded text-sky-500 shrink-0" style="font-size: 20px;">check_circle</span>
                AI palette suggestions (Mood/Usage)
              </li>
              <li class="flex gap-x-3 items-start">
                <span class="material-symbols-rounded text-sky-500 shrink-0" style="font-size: 20px;">check_circle</span>
                Priority support & early access
              </li>
              <li class="flex gap-x-3 items-start">
                <span class="material-symbols-rounded text-sky-500 shrink-0" style="font-size: 20px;">check_circle</span>
                2 months free equivalent
              </li>
            </ul>

            <button
              @click="startSubscription('power')"
              :disabled="loading === 'power' || !planIds.power"
              class="mt-8 block w-full rounded-full border border-zinc-300 bg-white px-3 py-3.5 text-center text-sm font-semibold text-zinc-700 hover:bg-zinc-50 hover:border-zinc-400 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700 transition-all active:scale-[0.98]">
              {{ loading === 'power' ? 'Redirecting...' : 'Choose Founder' }}
            </button>
          </article>
        </div>
      </section>

      <section class="mx-auto max-w-4xl px-6 pb-24 lg:px-8">
        <div class="text-center mb-10">
          <h2 class="text-2xl font-bold tracking-tight text-zinc-900 dark:text-white">Common Questions</h2>
        </div>

        <div class="space-y-4">
          <div
            v-for="(faq, index) in faqs"
            :key="index"
            class="rounded-2xl border border-zinc-200 bg-white/60 backdrop-blur-sm transition-all hover:bg-white/80 dark:border-zinc-800 dark:bg-zinc-900/40 dark:hover:bg-zinc-900/60 overflow-hidden">
            <button
              class="flex w-full items-center justify-between p-5 text-left text-zinc-900 dark:text-zinc-100"
              @click="toggleFaq(index)">
              <span class="text-sm font-semibold">{{ faq.q }}</span>
              <span
                class="material-symbols-rounded text-zinc-400 transition-transform duration-300"
                :class="{ 'rotate-180': activeFaq === index }">
                expand_more
              </span>
            </button>

            <div v-show="activeFaq === index" class="px-5 pb-5 pt-0 text-sm text-zinc-600 dark:text-zinc-400 animate-slideDown">
              <p>{{ faq.a }}</p>
            </div>
          </div>
        </div>
      </section>
    </main>

    <FooterBar />
  </div>
</template>

<script setup>
import HeaderNav from '../components/marketing/HeaderNav.vue'
import FooterBar from '../components/marketing/FooterBar.vue'
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const API = 'https://backend.nexgridco.com'

const loading = ref('')
const planIds = ref({ creator: '', power: '' })
const planPrices = ref({ pro: null, teams: null })
const planIntervals = ref({ pro: '', teams: '' })
const activeFaq = ref(null)

const toggleFaq = (index) => {
  activeFaq.value = activeFaq.value === index ? null : index
}

const priceText = (key) => {
  const cents = planPrices.value[key]
  if (typeof cents !== 'number') return '$—'
  return `$${(cents / 100).toFixed(0)}`
}

const tokenFromStorage = () => {
  try {
    const keys = ['token', 'jwt', 'access_token']
    for (const k of keys) {
      const v = localStorage.getItem(k) || sessionStorage.getItem(k)
      if (v) return v
    }
  } catch {
    // ignore
  }
  return ''
}

const authHeaders = () => {
  const headers = { 'Content-Type': 'application/json' }
  const tok = tokenFromStorage()
  if (tok) headers['Authorization'] = `Bearer ${tok}`
  return headers
}

function openAuthReplace() {
  const q = { ...route.query, auth: '1' }
  router.replace({ path: route.path, query: q, hash: route.hash })
}

const fetchPlans = async () => {
  try {
    const res = await fetch(`${API}/billing/plans`, {
      method: 'GET',
      headers: authHeaders(),
      credentials: 'include',
    })
    if (!res.ok) {
      if (res.status === 401) openAuthReplace()
      return
    }
    const data = await res.json()

    const monthly = data.find((p) => p.interval === 'month')
    const yearly = data.find((p) => p.interval === 'year')

    if (monthly) {
      planIds.value.creator = monthly.id
      planPrices.value.pro = monthly.amount ?? null
      planIntervals.value.pro = 'month'
    }
    if (yearly) {
      planIds.value.power = yearly.id
      planPrices.value.teams = yearly.amount ?? null
      planIntervals.value.teams = 'year'
    }
  } catch {
    // ignore
  }
}

onMounted(fetchPlans)

const startSubscription = async (key) => {
  const planId = planIds.value[key]
  if (!planId) {
    alert('Plan not configured')
    return
  }
  loading.value = key
  try {
    const res = await fetch(`${API}/billing/ensure`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify({ planId }),
    })
    if (res.status === 401) {
      openAuthReplace()
      return
    }
    if (!res.ok) {
      const t = await res.text()
      alert(t || 'Failed to start subscription')
      return
    }
    const data = await res.json()

    if (data?.url) {
      window.location.assign(data.url)
      return
    }
    if (data?.hasActive) {
      window.location.assign('/dashboard/subscription')
      return
    }
    alert('Checkout URL not received')
  } finally {
    loading.value = ''
  }
}

const faqs = ref([
  {
    q: 'Do you offer a free trial?',
    a: 'Yes. You get 1 day free to test all features. You can cancel anytime during the trial.',
  },
  {
    q: 'Are palette creations unlimited?',
    a: 'Yes. You can generate as many palettes and combinations as you need.',
  },
  {
    q: 'Do unused exports or projects expire?',
    a: 'No. Everything you create or save stays in your account permanently.',
  },
  {
    q: 'Can I switch between plans?',
    a: 'Yes. You can upgrade or downgrade at any time. Upgrades apply instantly, downgrades on renewal.',
  },
  {
    q: 'Is payment secure?',
    a: 'All payments are handled through Stripe with international security standards.',
  },
  {
    q: 'Is there any limit on templates?',
    a: 'No. All current and future templates are included in all plans.',
  },
  {
    q: 'Can I cancel my subscription?',
    a: 'Yes. You can cancel at any time directly from your dashboard.',
  },
])
</script>

<style scoped>
.material-symbols-rounded {
  font-variation-settings: "FILL" 0, "wght" 400, "GRAD" 0, "opsz" 24;
}

.animate-slideDown {
  animation: slideDown 0.2s ease-out forwards;
}

@keyframes slideDown {
  from { opacity: 0; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
